import axios, { AxiosError, AxiosResponse } from 'axios';
import { camelizeKeys } from 'humps';
import Cookies from 'js-cookie';

import { COOKIE_KEY_TOKEN, ROUTES } from '@/constants';

type ApiRequestParams = FormData | object | Blob;

//  If validator tool page fails sometime, consider this line.
//  'Content-Type': 'multipart/form-data',

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL as string,
  timeout: 60000,
  headers: {
    Accept: 'application/json',
  },
});

const getAuthToken = () => Cookies.get(COOKIE_KEY_TOKEN);
const removeAuthToken = () => Cookies.remove(COOKIE_KEY_TOKEN);

const createConfig = (params: ApiRequestParams = {}) => {
  const token = getAuthToken();
  return {
    ...(token && { headers: { Authorization: `Bearer ${token}` } }),
    ...params,
  };
};

export const getFromApi = async (
  url: string,
  params: ApiRequestParams = {}
) => {
  try {
    const response: AxiosResponse<object, object> = await apiClient.get(
      url,
      createConfig(params)
    );
    return response;
  } catch {
    throw new Error('failed to fetch from api');
  }
};

export const postToApi = async (url: string, data: ApiRequestParams) => {
  try {
    const response = await apiClient.post(url, data, createConfig());
    return response;
  } catch {
    throw new Error('failed to post to api');
  }
};

export const patchToApi = async (url: string, data: ApiRequestParams) => {
  try {
    const response = await apiClient.patch(url, data, createConfig());
    return response;
  } catch {
    throw new Error('failed to patch to api');
  }
};

export const deleteFromApi = async (url: string) => {
  try {
    const response = await apiClient.delete(url, createConfig());
    return response;
  } catch {
    throw new Error('failed to delete from api');
  }
};

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data) {
      response.data = camelizeKeys(response.data);
    }

    return response;
  },
  (error: AxiosError) => {
    const { response } = error;
    if (response && (response.status === 403 || response.status === 401)) {
      removeAuthToken();
      window.location.href = ROUTES.LOGIN;
    }
    return error;
  }
);
